import {
  CuripodElement,
  CuripodText,
  isCuripodElement,
  isCuripodText,
  parseStringToSlateObject,
} from '../components/SlideComponents/slateUtil';

export interface WordOffset {
  word: string;
  /** Start character index in the plain text */
  startChar: number;
  /** End character index (exclusive) in the plain text */
  endChar: number;
  /** Slate element index (top-level paragraph) */
  elementIdx: number;
  /** Slate child index within the element */
  childIdx: number;
  /** Character offset within the Slate text leaf where this word starts */
  slateCharStart: number;
  /** Character offset within the Slate text leaf where this word ends (exclusive) */
  slateCharEnd: number;
}

export interface WordTiming {
  word: string;
  startTime: number;
  endTime: number;
  elementIdx: number;
  childIdx: number;
  slateCharStart: number;
  slateCharEnd: number;
}

export interface ElevenLabsAlignment {
  characters: string[];
  character_start_times_seconds: number[];
  character_end_times_seconds: number[];
}

/**
 * Extract plain text from Slate JSON content, tracking each word's position
 * back to its Slate path for highlighting.
 */
export function extractPlainTextWithOffsets(
  slateContent: string,
): {
  plainText: string;
  wordMap: WordOffset[];
} {
  const elements = parseStringToSlateObject(slateContent);
  let plainText = '';
  const wordMap: WordOffset[] = [];

  for (let elementIdx = 0; elementIdx < elements.length; elementIdx++) {
    const element = elements[elementIdx];
    const children = getTextChildren(element);

    for (let childIdx = 0; childIdx < children.length; childIdx++) {
      const child = children[childIdx];
      if (!isCuripodText(child) || child.text.length === 0) continue;

      const text = child.text;
      const baseOffset = plainText.length;

      // Split text into words, preserving their positions
      const wordRegex = /\S+/g;
      let match: RegExpExecArray | null;
      while ((match = wordRegex.exec(text)) !== null) {
        wordMap.push({
          word: match[0],
          startChar: baseOffset + match.index,
          endChar: baseOffset + match.index + match[0].length,
          elementIdx,
          childIdx,
          slateCharStart: match.index,
          slateCharEnd: match.index + match[0].length,
        });
      }

      plainText += text;
    }

    // Add space between paragraphs
    if (elementIdx < elements.length - 1) {
      plainText += ' ';
    }
  }

  return { plainText, wordMap };
}

/**
 * Get the flat list of text children from a Slate element,
 * handling list-item nesting.
 */
function getTextChildren(element: CuripodElement): (CuripodText | CuripodElement)[] {
  if (!element.children) return [];

  const result: (CuripodText | CuripodElement)[] = [];
  for (const child of element.children) {
    if (isCuripodText(child)) {
      result.push(child);
    } else if (isCuripodElement(child) && child.type === 'list-item') {
      // Unwrap list items
      for (const grandchild of child.children) {
        if (isCuripodText(grandchild)) {
          result.push(grandchild);
        }
      }
    }
    // Skip math, prompt elements — they don't produce speakable text
  }
  return result;
}

/**
 * Map ElevenLabs character-level alignment data onto our word offsets
 * to produce word-level timings.
 */
export function buildWordTimings(
  alignment: ElevenLabsAlignment,
  wordMap: WordOffset[],
): WordTiming[] {
  if (!alignment || wordMap.length === 0) return [];

  const timings: WordTiming[] = [];

  for (const word of wordMap) {
    // Find the character timing for the start of this word
    const startCharIdx = word.startChar;
    const endCharIdx = word.endChar - 1; // Last character of the word

    // Clamp to alignment array bounds
    const safeStart = Math.min(startCharIdx, alignment.characters.length - 1);
    const safeEnd = Math.min(endCharIdx, alignment.characters.length - 1);

    if (safeStart < 0 || safeEnd < 0) continue;

    timings.push({
      word: word.word,
      startTime: alignment.character_start_times_seconds[safeStart] ?? 0,
      endTime: alignment.character_end_times_seconds[safeEnd] ?? 0,
      elementIdx: word.elementIdx,
      childIdx: word.childIdx,
      slateCharStart: word.slateCharStart,
      slateCharEnd: word.slateCharEnd,
    });
  }

  return timings;
}
